import React, { useState, useRef } from 'react'
import SafeIcon from '../../common/SafeIcon'
import { imageLibraryService } from '../../lib/imageLibraryService'
import { storageService } from '../../lib/storageService'
import { useAuth } from '../../contexts/AuthContext'
import * as FiIcons from 'react-icons/fi'

const { FiUpload, FiImage, FiX, FiCheck, FiAlertCircle } = FiIcons

const UploadZone = ({ onUploadComplete, onError, className = '' }) => {
  const { user } = useAuth()
  const fileInputRef = useRef(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState([])

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    )

    if (files.length > 0) {
      handleFiles(files)
    }
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) {
      handleFiles(files)
    }
    // Reset file input
    e.target.value = ''
  }

  const handleFiles = async (files) => {
    if (!user) {
      onError?.('Please log in to upload images')
      return
    }

    setUploading(true)
    const newProgress = files.map(file => ({
      name: file.name,
      status: 'uploading',
      progress: 0,
      error: null
    }))
    setUploadProgress(newProgress)

    const results = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      
      try {
        // Update progress
        setUploadProgress(prev => prev.map((item, index) => 
          index === i ? { ...item, status: 'uploading', progress: 25 } : item
        ))

        // Validate file
        const validation = storageService.validateFile(file)
        if (!validation.valid) {
          throw new Error(validation.errors.join(' '))
        }

        // Check quota
        const canUpload = await storageService.canUpload(user.id, file.size)
        if (!canUpload) {
          throw new Error('Storage quota exceeded. Please upgrade your plan or delete some images.')
        }

        // Update progress
        setUploadProgress(prev => prev.map((item, index) => 
          index === i ? { ...item, progress: 50 } : item
        ))

        // Upload image
        const uploadedImage = await imageLibraryService.uploadImage(user.id, file, {
          tags: ['upload'],
          description: '',
          altText: file.name
        })

        // Update progress
        setUploadProgress(prev => prev.map((item, index) => 
          index === i ? { ...item, status: 'success', progress: 100 } : item
        ))

        results.push(uploadedImage)

      } catch (error) {
        console.error('Upload error:', error)
        
        // Update progress with error
        setUploadProgress(prev => prev.map((item, index) => 
          index === i ? { 
            ...item, 
            status: 'error', 
            error: error.message,
            progress: 0 
          } : item
        ))

        onError?.(error.message)
      }
    }

    // Notify completion
    if (results.length > 0) {
      onUploadComplete?.(results)
    }

    // Clear progress after delay
    setTimeout(() => {
      setUploadProgress([])
      setUploading(false)
    }, 2000)
  }

  const removeProgressItem = (index) => {
    setUploadProgress(prev => prev.filter((_, i) => i !== index))
  }

  return (
    <div className={className}>
      {/* Upload Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-200 ${
          isDragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'pointer-events-none opacity-75' : 'cursor-pointer'}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        {/* Upload Icon and Text */}
        <div className="text-center">
          <SafeIcon 
            icon={FiUpload} 
            className={`mx-auto h-12 w-12 mb-4 ${
              isDragOver ? 'text-blue-500' : 'text-gray-400'
            }`} 
          />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isDragOver ? 'Drop images here' : 'Upload Images'}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Drag and drop your images here, or click to browse
          </p>
          <p className="text-xs text-gray-500">
            Supports JPEG, PNG, GIF, WebP, SVG • Max 10MB per file
          </p>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-gray-900">Uploading Files</h4>
          {uploadProgress.map((item, index) => (
            <UploadProgressItem
              key={`${item.name}-${index}`}
              item={item}
              onRemove={() => removeProgressItem(index)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const UploadProgressItem = ({ item, onRemove }) => {
  const getStatusIcon = () => {
    switch (item.status) {
      case 'success':
        return <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
      case 'error':
        return <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-red-500" />
      default:
        return <SafeIcon icon={FiImage} className="w-4 h-4 text-blue-500" />
    }
  }

  const getStatusColor = () => {
    switch (item.status) {
      case 'success':
        return 'bg-green-500'
      case 'error':
        return 'bg-red-500'
      default:
        return 'bg-blue-500'
    }
  }

  return (
    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
      {/* Status Icon */}
      <div className="flex-shrink-0">
        {getStatusIcon()}
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {item.name}
        </p>
        {item.error ? (
          <p className="text-xs text-red-600">{item.error}</p>
        ) : (
          <div className="mt-1">
            <div className="bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all duration-300 ${getStatusColor()}`}
                style={{ width: `${item.progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Remove Button */}
      {(item.status === 'success' || item.status === 'error') && (
        <button
          onClick={onRemove}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600"
        >
          <SafeIcon icon={FiX} className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

export default UploadZone
